import React, { useState, useEffect } from 'react';
import { featuresAPI } from '../../services/api';
import Card from '../common/Card';
import Loading from '../common/Loading';
import Button from '../common/Button';
import FeatureFormModal from './FeatureFormModal';
import { formatDate, handleApiError, showToast, showConfirm } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

const FeaturesListPage = () => {
  const { isRTL } = useLanguage();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await featuresAPI.getAll();
      setFeatures(response.data.features || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingFeature(null);
    setShowModal(true);
  };

  const handleEdit = (feature) => {
    setEditingFeature(feature);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm(
      'Are you sure you want to delete this feature? This action cannot be undone.',
      false
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(id);
      await featuresAPI.delete(id);
      await fetchFeatures();
      showToast.success('Feature deleted successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingFeature(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchFeatures();
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchFeatures} variant="secondary">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Features Management</h1>
          <p className="text-gray-600 mt-1">Manage dynamic features for subscription plans</p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          Add New Feature
        </Button>
      </div>

      {/* Features Grid */}
      {features.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-4">No features yet</p>
            <Button variant="primary" onClick={handleCreate}>
              Add Your First Feature
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <Card key={feature.id} className="p-6 relative">
              {/* Status Badge */}
              {feature.is_active === false && (
                <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                  <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                    Inactive
                  </span>
                </div>
              )}

              {/* Feature Header */}
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  {feature.icon && (
                    <span className="text-2xl mr-2">{feature.icon}</span>
                  )}
                  <h3 className="text-xl font-bold text-gray-900">{feature.name}</h3>
                </div>
                {feature.name_ar && (
                  <p className="text-sm text-gray-600">{feature.name_ar}</p>
                )}
              </div>

              {/* Description */}
              {feature.description && (
                <p className="text-gray-600 mb-4 text-sm">{feature.description}</p>
              )}

              {/* Display Order */}
              <div className="flex justify-between text-sm mb-4">
                <span className="text-gray-600">Display Order:</span>
                <span className="font-medium">{feature.display_order || 0}</span>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
                <span className="text-xs text-gray-500">
                  Created: {formatDate(feature.created_at)}
                </span>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => handleEdit(feature)}>
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(feature.id)}
                    disabled={deletingId === feature.id}
                  >
                    {deletingId === feature.id ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Feature Form Modal */}
      {showModal && (
        <FeatureFormModal
          feature={editingFeature}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default FeaturesListPage;
